<template>
  <SubscriptionNavigation
    @submit-clicked="onSubmit"
    :is-submitting-data="isSaveSubscriptionInProgress"
    :is-loading-data="isGetSubscriptionInProgress"
  >
    <div data-cy="billing-informations-page">
      <div class="text-center">
        <AtomText
          :text="t('subscription.billingInformations.title')"
          font-style="font-semibold"
          font-size="text-xl"
          data-cy="billing-informations-page-title"
        />
      </div>

      <OrganismAlert
        v-if="selectedLinearMonthlyPayment !== null"
        :title="
          t('subscription.billingInformations.alert.monthlyAmount', {
            amount: selectedLinearMonthlyPayment,
          })
        "
        :content-html="t('subscription.billingInformations.alert.description')"
        variant="complex"
        has-icon
        state="info"
        class="mt-6"
        data-cy="billing-informations-monthly-payment-alert"
      />

      <div data-cy="billing-informations-account-name-input">
        <MoleculeLabel
          :text="t('subscription.billingInformations.form.accountName.label')"
          label-for="accountName"
          class="mb-1 mt-6"
        />
        <MoleculeInputText
          v-model="accountName"
          id="accountName"
          name="accountName"
          class="w-full"
          :placeholder="t('subscription.billingInformations.form.accountName.placeholder')"
          :disabled="isSaveSubscriptionInProgress || isGetSubscriptionInProgress"
          :input-state="isSubmitted && getInputState({ errorMessage: errors.accountName })"
          :helper="
            isSubmitted &&
            errors.accountName &&
            getHelperMessage({ errorMessage: errors.accountName })
          "
        />
      </div>

      <div data-cy="billing-informations-iban-input">
        <MoleculeLabel
          :text="t('subscription.billingInformations.form.iban.label')"
          label-for="iban"
          class="mb-1 mt-6"
        />
        <MoleculeInputText
          v-model="iban"
          id="iban"
          name="iban"
          class="w-full"
          :placeholder="t('subscription.billingInformations.form.iban.placeholder')"
          :disabled="isSaveSubscriptionInProgress || isGetSubscriptionInProgress"
          :input-state="
            getInputState({
              errorMessage: isSubmitted ? errors.iban : undefined,
              isSuccess: isIbanValid,
            })
          "
          :helper="isSubmitted && errors.iban && getHelperMessage({ errorMessage: errors.iban })"
        />
      </div>

      <div data-cy="billing-informations-bic-input">
        <MoleculeLabel
          :text="t('subscription.billingInformations.form.bic.label')"
          label-for="bic"
          class="mb-1 mt-6"
        />
        <MoleculeInputText
          v-model="bic"
          id="bic"
          name="bic"
          class="w-full"
          :placeholder="t('subscription.billingInformations.form.bic.placeholder')"
          :disabled="isSaveSubscriptionInProgress || isGetSubscriptionInProgress"
          :input-state="
            getInputState({
              errorMessage: isSubmitted ? errors.bic : undefined,
              isSuccess: isBicValid,
            })
          "
          :helper="isSubmitted && errors.bic && getHelperMessage({ errorMessage: errors.bic })"
        />
      </div>

      <div data-cy="billing-informations-billing-address-toggle">
        <MoleculeLabel
          :text="t('subscription.billingInformations.form.isBillingAddress.label')"
          label-for="hasBillingAddress"
          class="mt-6 mb-1 font-medium"
        />
        <MoleculeToggle
          v-model="hasBillingAddress"
          :label="t('subscription.billingInformations.form.isBillingAddress.placeholder')"
          class="text-cime-gray-darker"
          label-position="right"
        />
      </div>

      <div v-show="hasBillingAddress" data-cy="billing-informations-billing-address-section">
        <div data-cy="billing-informations-billing-street-input">
          <MoleculeLabel
            :text="t('subscription.billingInformations.form.billingAddress.address.label')"
            label-for="billingAddressStreet"
            class="mb-1 mt-6"
          />
          <MoleculeInputText
            v-model="billingStreet"
            id="billingAddressStreet"
            name="billingAddressStreet"
            class="w-full"
            :placeholder="
              t('subscription.billingInformations.form.billingAddress.address.placeholder')
            "
            :disabled="isSaveSubscriptionInProgress || isGetSubscriptionInProgress"
            :input-state="
              isSubmitted && getInputState({ errorMessage: billingAddressErrors.address })
            "
            :helper="
              isSubmitted &&
              billingAddressErrors.address &&
              getHelperMessage({ errorMessage: billingAddressErrors.address })
            "
          />
        </div>

        <div data-cy="billing-informations-billing-complement-input">
          <MoleculeLabel
            :text="
              t('subscription.billingInformations.form.billingAddress.addressComplement.label')
            "
            :optional-text="t('common.forms.optional')"
            label-for="billingAddressComplement"
            class="mb-1 mt-6"
          />
          <MoleculeInputText
            v-model="billingComplement"
            id="billingAddressComplement"
            name="billingAddressComplement"
            class="w-full"
            :placeholder="
              t(
                'subscription.billingInformations.form.billingAddress.addressComplement.placeholder'
              )
            "
            :disabled="isSaveSubscriptionInProgress || isGetSubscriptionInProgress"
          />
        </div>

        <div class="grid grid-cols-2 gap-4">
          <div data-cy="billing-informations-billing-zip-code-input">
            <MoleculeLabel
              :text="t('subscription.billingInformations.form.billingAddress.zipCode.label')"
              label-for="billingAddressZipCode"
              class="mb-1 mt-6"
            />
            <MoleculeInputText
              v-model="billingZipCode"
              id="billingAddressZipCode"
              name="billingAddressZipCode"
              class="w-full"
              :placeholder="
                t('subscription.billingInformations.form.billingAddress.zipCode.placeholder')
              "
              :disabled="isSaveSubscriptionInProgress || isGetSubscriptionInProgress"
              :input-state="
                isSubmitted && getInputState({ errorMessage: billingAddressErrors.zipCode })
              "
              :helper="
                isSubmitted &&
                billingAddressErrors.zipCode &&
                getHelperMessage({ errorMessage: billingAddressErrors.zipCode })
              "
            />
          </div>
          <div data-cy="billing-informations-billing-city-input">
            <MoleculeLabel
              :text="t('subscription.billingInformations.form.billingAddress.city.label')"
              label-for="billingAddressCity"
              class="mb-1 mt-6"
            />
            <MoleculeInputText
              v-model="billingCity"
              id="billingAddressCity"
              name="billingAddressCity"
              class="w-full"
              :placeholder="
                t('subscription.billingInformations.form.billingAddress.city.placeholder')
              "
              :disabled="isSaveSubscriptionInProgress || isGetSubscriptionInProgress"
              :input-state="
                isSubmitted && getInputState({ errorMessage: billingAddressErrors.city })
              "
              :helper="
                isSubmitted &&
                billingAddressErrors.city &&
                getHelperMessage({ errorMessage: billingAddressErrors.city })
              "
            />
          </div>
        </div>
      </div>
    </div>
  </SubscriptionNavigation>
</template>

<script setup lang="ts">
import { extractIBAN, isSEPACountry, isValidIBAN } from 'ibantools';
import { storeToRefs } from 'pinia';
import { useForm } from 'vee-validate';
import { computed, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import * as yup from 'yup';

import useInputState from '@/composables/useInputState.composable';
import { useLoadMaps } from '@/composables/useLoadMaps.composable';
import { useStepStore, useSubscriptionStore, useToastSnackbarStore } from '@/stores';

import type { SubscriptionAddress } from '@/types/subscription.type';
import { getErrorToasterConfig } from '@/utils/error.util';
import type { GoogleAddress } from '@/utils/geocoderAddress.util';
import { bicRegex } from '@/utils/regex.util';

import SubscriptionNavigation from '@/components/SubscriptionNavigation.component.vue';

const { t } = useI18n();
const toastSnackBar = useToastSnackbarStore();
const subscriptionStore = useSubscriptionStore();
const stepStore = useStepStore();

const { getInputState, getHelperMessage } = useInputState();
const { loadMaps, initAddressAutocomplete } = useLoadMaps();

const { goToNextStep } = stepStore;

const {
  isGetSubscriptionInProgress,
  isSaveSubscriptionInProgress,
  currentSubscription,
  getCurrentDirectDebit,
} = storeToRefs(subscriptionStore);

const selectedLinearMonthlyPayment = computed(
  () => getCurrentDirectDebit.value?.selectedLinearMonthlyPayment ?? null
);

const billingInformationsSchema = yup.object({
  accountName: yup.string().required(t('common.forms.required')),
  iban: yup
    .string()
    .trim()
    .required(t('common.forms.required'))
    .test('iban-valid', t('subscription.billingInformations.form.iban.helper'), value => {
      return isValidIBAN(value?.replace(/ +/g, '') ?? '');
    })
    .test(
      'iban-country-valid',
      t('subscription.billingInformations.form.iban.countryHelper'),
      value => {
        const extracted = extractIBAN(value ?? '');
        return extracted.countryCode ? isSEPACountry(extracted.countryCode) : false;
      }
    ),
  bic: yup
    .string()
    .required(t('common.forms.required'))
    .matches(bicRegex, t('subscription.billingInformations.form.bic.helper')),
});

const billingAddressSchema = yup.object({
  address: yup.string().required(t('common.forms.required')),
  addressComplement: yup.string(),
  zipCode: yup.string().required(t('common.forms.required')),
  city: yup.string().required(t('common.forms.required')),
});

const existingBillingInfo = currentSubscription.value?.billingInformations;

const {
  errors,
  defineField,
  validate: validateBillingInformations,
} = useForm({
  validationSchema: billingInformationsSchema,
  initialValues: {
    accountName: existingBillingInfo?.accountName ?? '',
    iban: existingBillingInfo?.iban ?? '',
    bic: existingBillingInfo?.bic ?? '',
  },
});

const [accountName] = defineField('accountName');
const [iban] = defineField('iban');
const [bic] = defineField('bic');

const {
  errors: billingAddressErrors,
  defineField: defineBillingAddressField,
  validate: validateBillingAddress,
} = useForm<SubscriptionAddress>({
  validationSchema: billingAddressSchema,
  initialValues: {
    address: existingBillingInfo?.billingAddress?.address ?? '',
    addressComplement: existingBillingInfo?.billingAddress?.addressComplement ?? '',
    zipCode: existingBillingInfo?.billingAddress?.zipCode ?? '',
    city: existingBillingInfo?.billingAddress?.city ?? '',
  },
});

const [billingStreet] = defineBillingAddressField('address');
const [billingComplement] = defineBillingAddressField('addressComplement');
const [billingZipCode] = defineBillingAddressField('zipCode');
const [billingCity] = defineBillingAddressField('city');

const hasBillingAddress = ref(
  !!existingBillingInfo?.billingAddress?.address &&
    existingBillingInfo.billingAddress.address !==
      currentSubscription.value?.installationAddress?.address
);

const isIbanValid = computed(() => {
  const normalized = iban.value?.trim().replace(/ +/g, '') ?? '';
  if (!isValidIBAN(normalized)) return false;
  const extracted = extractIBAN(normalized);
  return extracted.countryCode ? isSEPACountry(extracted.countryCode) : false;
});

const isBicValid = computed(() => new RegExp(bicRegex).test(bic.value ?? ''));

const isSubmitted = ref(false);

const setBillingAddressFromGoogleApi = (fullAddress: GoogleAddress) => {
  billingStreet.value = fullAddress.address;
  billingZipCode.value = fullAddress.postalCode;
  billingCity.value = fullAddress.city;
};

const onSubmit = async () => {
  isSubmitted.value = true;
  toastSnackBar.clearToasts();

  const { valid: billingInfoValid } = await validateBillingInformations();
  if (!billingInfoValid) return;

  if (hasBillingAddress.value) {
    const { valid: billingAddressValid } = await validateBillingAddress();
    if (!billingAddressValid) return;
  }

  const installationAddress = currentSubscription.value?.installationAddress;
  const billingAddressPayload: SubscriptionAddress = hasBillingAddress.value
    ? {
        address: billingStreet.value,
        addressComplement: billingComplement.value || undefined,
        zipCode: billingZipCode.value,
        city: billingCity.value,
      }
    : {
        address: installationAddress?.address ?? '',
        addressComplement: installationAddress?.addressComplement,
        zipCode: installationAddress?.zipCode ?? '',
        city: installationAddress?.city ?? '',
      };

  try {
    await subscriptionStore.updateSubscriptionWithBillingInformations({
      accountName: accountName.value,
      iban: iban.value.trim().replace(/ +/g, ''),
      bic: bic.value,
      billingAddress: billingAddressPayload,
    });
    goToNextStep();
  } catch (error) {
    const toastConfig = getErrorToasterConfig({
      error,
      toasterId: 'billing-informations-error',
      defaultMessage: t(
        'subscription.billingInformations.api.errors.patchSubscriptionBillingInformations'
      ),
    });
    toastSnackBar.addToast(toastConfig);
  }
};

onMounted(async () => {
  await loadMaps(['places']);
  initAddressAutocomplete('billingAddressStreet', setBillingAddressFromGoogleApi);
});
</script>

<style scoped></style>
